import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: t("nav.home"), href: "#home" },
    { label: t("nav.services"), href: "#services" },
    { label: t("nav.solutions"), href: "#solutions" },
    { label: t("nav.about"), href: "#why" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const scrollToSection = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-xl border-b border-foreground-100/60"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 lg:px-12 xl:px-20">
        <div className="flex items-center justify-between h-[72px]">
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#home");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20technology%20logo%20mark%2C%20interlocking%20hexagonal%20shapes%20forming%20a%20sleek%20minimalist%20emblem%2C%20warm%20terracotta%20copper%20gradient%20on%20white%20background%2C%20clean%20modern%20corporate%20identity%20design%2C%20flat%20vector%20style%2C%20sharp%20precise%20geometric%20lines%2C%20premium%20tech%20branding&width=80&height=80&seq=emporris-brand-mark-v2&orientation=squarish"
              alt="Emporris"
              className="h-9 w-9 object-contain flex-shrink-0"
            />
            <span
              className={`text-lg font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? "text-foreground-950" : "text-white"
              }`}
            >
              Emporris
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "text-foreground-600 hover:text-foreground-950 hover:bg-foreground-50"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#contact");
              }}
              className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-foreground-950 text-white hover:bg-foreground-800"
                  : "bg-white text-foreground-950 hover:bg-white/90"
              }`}
            >
              {t("nav.cta")}
            </a>
          </div>

          <button
            className={`lg:hidden p-2 rounded-lg cursor-pointer transition-colors ${
              scrolled ? "text-foreground-700 hover:bg-foreground-50" : "text-white hover:bg-white/10"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              {mobileOpen ? (
                <i className="ri-close-line text-xl" />
              ) : (
                <i className="ri-menu-line text-xl" />
              )}
            </div>
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[500px]" : "max-h-0"
        }`}
      >
        <div className="bg-background-50/95 backdrop-blur-xl border-t border-foreground-100/60 px-6 py-5 space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="block px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-foreground-50 rounded-lg transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <div className="pt-3">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#contact");
              }}
              className="block w-full px-4 py-3 text-sm font-semibold text-center text-white bg-foreground-950 hover:bg-foreground-800 rounded-lg transition-colors cursor-pointer"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}